import {
  DefaultError,
  InvalidateOptions,
  InvalidateQueryFilters,
  MutationKey,
  MutationOptions,
  QueryClient,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  useIsMutating,
  useQuery,
} from "@tanstack/react-query";

export * as q from "./queries";
import {HTTPError} from "platformed-client";
import {RedirectError} from "../api";
import {deferInvalidation, directlyInvalidateQuery} from "./invalidate";
import {useSuspenseQueriesWithDiagnostics, useSuspenseQueryWithDiagnostics} from "../utils/diagnostics";
import {Nominal} from "platformed-client/types";
import {useRef} from "react";

function shouldRetry(failureCount: number, error: any) {
  if (failureCount >= 3) {
    return false;
  } else if (error instanceof RedirectError) {
    return false;
  } else if (error instanceof HTTPError && error.response.status < 500) {
    return false;
  } else {
    return true;
  }
}

export const queryClient = new QueryClient({defaultOptions: {queries: {retry: shouldRetry, staleTime: 5000}}});

export type QueryData<T> = T extends {queryFn: (...args: any[]) => Promise<infer D>} ? D : never;

export function useQueryData<const T, TData = QueryData<T>>(options: T): TData {
  return useSuspenseQueryWithDiagnostics(options as any).data as TData;
}

export function useQueryState<const T, TData = QueryData<T>>(options: T): UseQueryResult<TData> {
  return useQuery(options as UseQueryOptions<any, any, TData>);
}

type CombinedQueriesData<T extends readonly any[]> = {[K in keyof T]: QueryData<T[K]>};

export function useQueriesData<T extends readonly any[]>({
  queries,
  preservePreviousData = false,
}: {
  queries: T;
  /**
   * Enable stale-while-revalidate behaviour:
   * - If `true`, previous data will be returned while new data is being loaded.
   * - If `false`, this hook will suspend while loading new data.
   *
   * Only set this to `true` if the order and identity of queries remain
   * consistent between renders. If the queries change (e.g., from `[1, 2, 3]`
   * to `[2, 3, 4]`), leave this as `false` to avoid mismatched data.
   */
  preservePreviousData?: boolean;
}): CombinedQueriesData<T> {
  const prevResult = useRef<CombinedQueriesData<T> | undefined>(undefined);

  try {
    const queryResults = useSuspenseQueriesWithDiagnostics({queries: queries.map(options => ({...options}))});

    const newData = queryResults.map(item => item.data) as CombinedQueriesData<T>;

    // Return the same array instance if the returned query data is the same.
    if (prevResult.current !== undefined) {
      const oldData = prevResult.current;
      const isSame = newData.length === oldData.length && newData.every((item, index) => item === oldData[index]);

      if (isSame) {
        return prevResult.current;
      }
    }

    prevResult.current = newData;
    return newData;
  } catch (ex) {
    if (ex instanceof Promise && prevResult.current !== undefined && preservePreviousData) {
      return prevResult.current;
    }
    throw ex;
  }
}

export type PlatformedQueryFilters = Nominal<InvalidateQueryFilters, "platformedQueryFilters">;

// Marks active queries matching the filter to be re-fetched, but leaves the existing data in the cache until
// the re-fetch has completed.
export async function invalidateQueries(filters?: PlatformedQueryFilters[]) {
  await deferInvalidation(() =>
    Promise.all((filters ?? [undefined]).map(filter => directlyInvalidateQuery(filter as PlatformedQueryFilters))),
  );
}

// Resets all the queries matching the filter to their initial state, clearing matching cache keys.
export async function resetQueries(filters?: PlatformedQueryFilters[], options?: InvalidateOptions) {
  await Promise.all(
    (filters ?? [undefined]).map(filter => queryClient.resetQueries(filter as InvalidateQueryFilters, options)),
  );
}

// Directly store data in the cache under the given key.
export async function setQueryData<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  updater: TData | undefined | ((oldData: TData | undefined) => TData | undefined),
) {
  queryClient.setQueryData<TData>(options.queryKey, updater);
}

export async function mutate<TData = unknown, TError = DefaultError, TContext = unknown>(
  options: MutationOptions<TData, TError, void, TContext>,
) {
  queryClient.getMutationCache().build(queryClient, options).execute(undefined);
}

export function useMutationDone({mutationKey}: {mutationKey: MutationKey}) {
  const isMutationInProgress = useIsMutating({mutationKey});
  if (isMutationInProgress) {
    const cache = queryClient.getMutationCache();
    throw new Promise(resolve => {
      const unsubscribe = cache.subscribe(() => {
        if (cache.findAll({mutationKey}).every(mutation => mutation.state.status !== "pending")) {
          unsubscribe();
          resolve(null);
        }
      });
    });
  }
}
